import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const EcommerceCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    discount: "",
    termsAndConditions: "",
    upTo: "",
    validUntil: "",
    usageLimit: "",
    image: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/v1/admin/coupons");
      setCoupons(response.data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axiosInstance.post("/api/v1/admin/coupons", formData);
      setModal(false);
      fetchCoupons();
      setFormData({
        name: "",
        title: "",
        discount: "",
        termsAndConditions: "",
        upTo: "",
        validUntil: "",
        usageLimit: "",
        image: "",
      });
    } catch (error) {
      console.error("Error creating coupon:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this coupon?")) {
      try {
        await axiosInstance.delete(`/api/v1/admin/coupons/${id}`);
        fetchCoupons();
      } catch (error) {
        console.error("Error deleting coupon:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const uploadImageToOm = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const imageUrl = await uploadImageToOm(file);
        setFormData({ ...formData, image: imageUrl });
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Coupons"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "Coupons", link: "#" },
          ]}
        />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Coupons List</h4>
                  <Button color="primary" onClick={() => setModal(true)}>
                    Add New Coupon
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center my-5">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Code</th>
                          <th>Title</th>
                          <th>Discount</th>
                          <th>Usage</th>
                          <th>Valid Until</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.map((coupon) => (
                          <tr key={coupon._id}>
                            <td>
                              <Link
                                to={`/coupon-details/${coupon._id}`}
                                className="text-dark fw-medium"
                              >
                                {coupon.name}
                              </Link>
                            </td>
                            <td>{coupon.title}</td>
                            <td>
                              {coupon.discount}% up to{" "}
                              {formatCurrency(coupon.upTo)}
                            </td>
                            <td>
                              <div>
                                <p className="mb-0">
                                  {coupon.totalUsers} users
                                </p>
                                <small className="text-muted">
                                  {formatCurrency(coupon.totalDiscount)} total
                                  discount
                                </small>
                              </div>
                            </td>
                            <td>{formatDate(coupon.validUntil)}</td>
                            <td>
                              <Badge
                                color={
                                  new Date(coupon.validUntil) > new Date()
                                    ? "success"
                                    : "danger"
                                }
                              >
                                {new Date(coupon.validUntil) > new Date()
                                  ? "Active"
                                  : "Expired"}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-3">
                                <Link
                                  to={`/coupon-details/${coupon._id}`}
                                  className="text-success"
                                >
                                  <i className="mdi mdi-pencil font-size-18" />
                                </Link>
                                <Link
                                  to="#"
                                  className="text-danger"
                                  onClick={() => handleDelete(coupon._id)}
                                >
                                  <i className="mdi mdi-delete font-size-18" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Add Coupon Modal */}
        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)}>
            Add New Coupon
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="name">Coupon Code</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g., SUMMER2024"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="e.g., Summer Sale Discount"
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="discount">Discount Percentage</Label>
                    <Input
                      id="discount"
                      type="number"
                      value={formData.discount}
                      onChange={(e) =>
                        setFormData({ ...formData, discount: e.target.value })
                      }
                      placeholder="e.g., 10"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="upTo">Maximum Discount</Label>
                    <Input
                      id="upTo"
                      type="number"
                      value={formData.upTo}
                      onChange={(e) =>
                        setFormData({ ...formData, upTo: e.target.value })
                      }
                      placeholder="e.g., 1000"
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="validUntil">Valid Until</Label>
                    <Input
                      id="validUntil"
                      type="datetime-local"
                      value={formData.validUntil}
                      onChange={(e) =>
                        setFormData({ ...formData, validUntil: e.target.value })
                      }
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="usageLimit">Usage Limit</Label>
                    <Input
                      id="usageLimit"
                      type="number"
                      value={formData.usageLimit}
                      onChange={(e) =>
                        setFormData({ ...formData, usageLimit: e.target.value })
                      }
                      placeholder="Leave empty for unlimited"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="termsAndConditions">Terms & Conditions</Label>
                <Input
                  id="termsAndConditions"
                  type="textarea"
                  rows="4"
                  value={formData.termsAndConditions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      termsAndConditions: e.target.value,
                    })
                  }
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="image">Upload Image</Label>
                <Input
                  id="image"
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                {formData.image && (
                  <div className="mt-2">
                    <img
                      src={formData.image}
                      alt="Coupon"
                      style={{ maxWidth: "100px" }}
                    />
                  </div>
                )}
              </FormGroup>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  color="light"
                  className="me-2"
                  onClick={() => setModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={submitting}>
                  {submitting ? <Spinner size="sm" /> : "Create Coupon"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default EcommerceCoupons;
